import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Loader from "../../../components/ui/Loader";
import { useGetProfileQuery } from "../../../store/api/authApi";
import { useListPublicPaymentAccountsQuery } from "../../../store/api/paymentAccountsApi";
import type { Payment } from "../../../store/api/paymentsApi";
import { useListPaymentsQuery } from "../../../store/api/paymentsApi";
import PaymentProofUploadModal from "./PaymentProofUploadModal";

const PaymentSection: React.FC = () => {
	const navigate = useNavigate();
	const { data: userProfile } = useGetProfileQuery();
	const {
		data: paymentsData,
		isLoading,
		refetch,
	} = useListPaymentsQuery({
		page: 1,
		page_size: 50,
	});

	const {
		data: paymentAccountsData,
		isLoading: isLoadingAccounts,
		error: paymentAccountsError,
	} = useListPublicPaymentAccountsQuery();

	const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
	const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

	// Backend already filters by tenant, so all results are tenant's payments
	const payments = paymentsData?.results || [];
	const pendingPayments = payments.filter((p) => p.status === "pending");
	const paidPayments = payments.filter((p) => p.status === "paid");
	const activePaymentAccounts = paymentAccountsData?.results || [];

	const formatCurrency = (amount: number) => {
		return new Intl.NumberFormat("en-NG", {
			style: "currency",
			currency: "NGN",
		}).format(amount);
	};

	const formatDate = (dateString?: string) => {
		if (!dateString) return "N/A";
		return new Date(dateString).toLocaleDateString("en-US", {
			month: "short",
			day: "numeric",
			year: "numeric",
		});
	};

	const getDaysUntilDue = (dueDateString?: string) => {
		if (!dueDateString) return null;
		const dueDate = new Date(dueDateString);
		const today = new Date();
		const diffTime = dueDate.getTime() - today.getTime();
		const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
		return diffDays;
	};

	const getStatusBadge = (status: string, dueDate?: string) => {
		const baseClasses = "px-2 py-1 text-xs font-medium rounded-full capitalize";
		const isOverdue =
			status === "pending" && dueDate && new Date(dueDate) < new Date();

		if (isOverdue || status === "overdue") {
			return `${baseClasses} bg-error/10 text-error`;
		}

		switch (status) {
			case "paid":
				return `${baseClasses} bg-success/10 text-success`;
			case "pending":
				return `${baseClasses} bg-warning/10 text-warning`;
			default:
				return `${baseClasses} bg-muted text-muted-foreground`;
		}
	};

	const handleUploadProof = (payment: Payment) => {
		setSelectedPayment(payment);
		setIsUploadModalOpen(true);
	};

	const handleUploadSuccess = () => {
		setIsUploadModalOpen(false);
		setSelectedPayment(null);
		refetch();
	};

	if (isLoading || isLoadingAccounts) {
		return (
			<Loader size="md" showText text="Loading payments..." className="py-12" />
		);
	}

	const nextPayment = pendingPayments.sort(
		(a, b) =>
			new Date(a.due_date || "").getTime() -
			new Date(b.due_date || "").getTime(),
	)[0];
	const daysUntilDue = nextPayment
		? getDaysUntilDue(nextPayment.due_date)
		: null;

	return (
		<div className="space-y-6">
			{/* Payment Account Details - Always show if accounts exist */}
			{activePaymentAccounts.length > 0 && (
				<div className="bg-card border border-border rounded-lg card-shadow">
					<div className="p-6 border-b border-border">
						<div className="flex items-center justify-between">
							<div>
								<div className="flex items-center space-x-2">
									<Icon name="CreditCard" size={20} className="text-primary" />
									<h2 className="text-xl font-semibold text-foreground">
										Payment Account Details
									</h2>
								</div>
								<p className="text-sm text-muted-foreground mt-1">
									Send your payment to one of the following accounts
								</p>
							</div>
							{/* Quick Upload Button */}
							{nextPayment && !nextPayment.payment_proof_url && (
								<Button
									onClick={() => handleUploadProof(nextPayment)}
									variant="outline"
									size="sm"
									className="flex items-center space-x-2"
								>
									<Icon name="Upload" size={16} />
									<span className="hidden sm:inline">Upload Proof</span>
								</Button>
							)}
						</div>
					</div>

					<div className="p-6">
						<div className="space-y-4">
							{activePaymentAccounts.map((account) => (
								<div
									key={account.id}
									className="bg-muted/30 rounded-lg p-4 border border-border"
								>
									<div className="flex items-start justify-between mb-3">
										<div className="flex-1">
											<h3 className="font-semibold text-foreground mb-2">
												{account.account_name}
											</h3>
											<div className="space-y-2 text-sm">
												<div className="flex items-center space-x-2">
													<span className="text-muted-foreground min-w-[120px]">
														Account Number:
													</span>
													<span className="font-mono font-medium text-foreground">
														{account.account_number}
													</span>
												</div>
												<div className="flex items-center space-x-2">
													<span className="text-muted-foreground min-w-[120px]">
														Bank Name:
													</span>
													<span className="text-foreground font-medium">
														{account.bank_name}
													</span>
												</div>
												{account.notes && (
													<div className="mt-3 pt-3 border-t border-border">
														<p className="text-muted-foreground text-xs">
															{account.notes}
														</p>
													</div>
												)}
											</div>
										</div>
									</div>
								</div>
							))}
						</div>
					</div>
				</div>
			)}

			{/* Current Payment Due */}
			{nextPayment && (
				<div className="bg-card border border-border rounded-lg card-shadow">
					<div className="p-6 border-b border-border">
						<h2 className="text-xl font-semibold text-foreground">
							Current Payment Due
						</h2>
					</div>

					<div className="p-6">
						<div
							className={`p-6 rounded-lg border-2 ${
								daysUntilDue !== null && daysUntilDue <= 3
									? "bg-error/5 border-error/20"
									: daysUntilDue !== null && daysUntilDue <= 7
									? "bg-warning/5 border-warning/20"
									: "bg-primary/5 border-primary/20"
							}`}
						>
							<div className="flex items-center justify-between mb-4">
								<div>
									<h3 className="text-2xl font-bold text-foreground">
										{formatCurrency(nextPayment.amount)}
									</h3>
									<p className="text-muted-foreground">
										Due: {formatDate(nextPayment.due_date)}
									</p>
								</div>

								<div className="text-right">
									<div
										className={`text-sm font-medium ${
											daysUntilDue !== null && daysUntilDue <= 0
												? "text-error"
												: daysUntilDue !== null && daysUntilDue <= 3
												? "text-warning"
												: "text-primary"
										}`}
									>
										{daysUntilDue === null
											? "Due date not set"
											: daysUntilDue <= 0
											? "Overdue"
											: daysUntilDue === 1
											? "Due tomorrow"
											: `Due in ${daysUntilDue} days`}
									</div>
								</div>
							</div>

							{nextPayment.payment_proof_url ? (
								<div className="mb-4 p-3 bg-success/10 rounded-lg">
									<div className="flex items-center space-x-2 text-success">
										<Icon name="CheckCircle" size={16} />
										<span className="text-sm font-medium">
											Payment proof uploaded - Awaiting verification
										</span>
									</div>
									{nextPayment.payment_proof_uploaded_at && (
										<p className="text-xs text-muted-foreground mt-1">
											Uploaded:{" "}
											{formatDate(nextPayment.payment_proof_uploaded_at)}
										</p>
									)}
								</div>
							) : (
								<Button
									onClick={() => handleUploadProof(nextPayment)}
									className="w-full flex items-center justify-center space-x-2"
									size="lg"
								>
									<Icon name="Upload" size={20} />
									<span>Upload Payment Proof</span>
								</Button>
							)}
						</div>
					</div>
				</div>
			)}

			{/* Pending Payments */}
			{pendingPayments.length > 0 && (
				<div className="bg-card border border-border rounded-lg card-shadow">
					<div className="p-6 border-b border-border">
						<h3 className="font-semibold text-foreground">Pending Payments</h3>
						<p className="text-muted-foreground text-sm mt-1">
							Payments awaiting proof upload or verification
						</p>
					</div>

					<div className="p-6">
						<div className="space-y-4">
							{pendingPayments.map((payment) => (
								<div
									key={payment.id}
									className="flex items-center justify-between p-4 border border-border rounded-lg"
								>
									<div className="flex-1">
										<div className="flex items-center justify-between mb-2">
											<h4 className="font-medium text-foreground">
												{formatCurrency(payment.amount)}
											</h4>
											<span
												className={getStatusBadge(
													payment.status,
													payment.due_date,
												)}
											>
												{payment.due_date &&
												new Date(payment.due_date) < new Date() &&
												payment.status === "pending"
													? "Overdue"
													: payment.status}
											</span>
										</div>
										<p className="text-sm text-muted-foreground">
											Due: {formatDate(payment.due_date)}
										</p>
										{payment.reference_number && (
											<p className="text-xs text-muted-foreground mt-1">
												Ref: {payment.reference_number}
											</p>
										)}
										{payment.payment_proof_url && (
											<div className="mt-2 flex items-center space-x-2 text-xs text-success">
												<Icon name="CheckCircle" size={14} />
												<span>Proof uploaded - Awaiting verification</span>
											</div>
										)}
									</div>

									<div className="ml-4">
										{!payment.payment_proof_url ? (
											<Button
												variant="outline"
												size="sm"
												onClick={() => handleUploadProof(payment)}
											>
												<Icon name="Upload" size={16} className="mr-2" />
												Upload Proof
											</Button>
										) : (
											<a
												href={payment.payment_proof_url}
												target="_blank"
												rel="noopener noreferrer"
												className="text-primary hover:text-primary/80 text-sm flex items-center space-x-1"
											>
												<Icon name="ExternalLink" size={14} />
												<span>View Proof</span>
											</a>
										)}
									</div>
								</div>
							))}
						</div>
					</div>
				</div>
			)}

			{/* Payment History */}
			<div className="bg-card border border-border rounded-lg card-shadow">
				<div className="p-6 border-b border-border">
					<div className="flex items-start justify-between gap-4">
						<div>
							<h3 className="font-semibold text-foreground">Payment History</h3>
							<p className="text-muted-foreground text-sm mt-1">
								Your recent payment transactions
							</p>
						</div>
						<Button
							variant="outline"
							size="sm"
							onClick={() => navigate("/payment-proof-upload")}
							className="flex items-center gap-1.5 flex-shrink-0"
						>
							<Icon name="Upload" size={14} />
							<span className="hidden sm:inline">Upload Proof</span>
						</Button>
					</div>
				</div>

				<div className="p-6">
					{paidPayments.length > 0 ? (
						<div className="space-y-4">
							{paidPayments
								.sort(
									(a, b) =>
										new Date(b.paid_date || "").getTime() -
										new Date(a.paid_date || "").getTime(),
								)
								.map((payment) => (
									<div
										key={payment.id}
										className="flex items-center justify-between p-4 border border-border rounded-lg"
									>
										<div className="flex items-center space-x-4">
											<div className="p-2 bg-success/10 rounded-lg">
												<Icon
													name="CheckCircle"
													size={20}
													className="text-success"
												/>
											</div>

											<div>
												<h4 className="font-medium text-foreground">
													{formatCurrency(payment.amount)}
												</h4>
												<p className="text-sm text-muted-foreground">
													{formatDate(payment.paid_date || payment.due_date)} •{" "}
													{payment.payment_method
														?.replace("_", " ")
														.charAt(0)
														.toUpperCase()}
													{payment.payment_method?.replace("_", " ").slice(1)}
												</p>
												{payment.reference_number && (
													<p className="text-xs text-muted-foreground">
														Ref: {payment.reference_number}
													</p>
												)}
											</div>
										</div>

										<div className="text-right">
											<span className={getStatusBadge(payment.status)}>
												{payment.status}
											</span>
										</div>
									</div>
								))}
						</div>
					) : (
						<div className="text-center py-8">
							<Icon
								name="CreditCard"
								size={48}
								className="mx-auto mb-4 text-muted-foreground opacity-50"
							/>
							<h4 className="font-medium text-foreground mb-2">
								No payment history
							</h4>
							<p className="text-muted-foreground">
								Your payment history will appear here once payments are
								verified.
							</p>
						</div>
					)}
				</div>
			</div>

			{/* Upload Modal */}
			<PaymentProofUploadModal
				payment={selectedPayment}
				isOpen={isUploadModalOpen}
				onClose={() => {
					setIsUploadModalOpen(false);
					setSelectedPayment(null);
				}}
				onSuccess={handleUploadSuccess}
			/>
		</div>
	);
};

export default PaymentSection;
